import React from 'react';
import get from 'lodash/get';
import {
  Card,
  Stack,
  Text,
  Tooltip,
  Icon,
  AutoSavedRadioButtonGroupControl,
} from '@piwikpro/ui-components';
import { withInject, Action } from '@piwikpro/platform';
import {
  TrackerSettings,
  TrackerSettingsResponse,
} from '../../../TrackerSettings';

export interface PrivacyProps {
  appTrackerSettings: TrackerSettingsResponse
  globalTrackerSettings: TrackerSettingsResponse
  trackerSettings: TrackerSettings
  appId: string
  t: (key: string) => string
}

enum anonymizeVisitorIpLevels {
  noAnonymize = 0,
  firstByte = 1,
  firstTwoBytes = 2,
  firstThreeBytes = 3,
  firstFourBytes = 4,
}

const DEFAULT_VALUES = {
  set_ip_tracking: true,
  anonymize_visitor_ip_level: anonymizeVisitorIpLevels.firstTwoBytes,
  anonymize_visitor_geolocation_level: 'none',
} as any;

export class Privacy extends React.Component<PrivacyProps> {
  private readonly geolocationLevels: Array<any>;

  constructor(props: PrivacyProps) {
    super(props);
    const { t } = props;

    this.handleUpdateSettings = this.handleUpdateSettings.bind(this);
    this.handleSubmitCheckbox = this.handleSubmitCheckbox.bind(this);

    this.geolocationLevels = [
      {
        id: 'none',
        value: 'none',
        name: t('trackerSettings.privacy.anonimization.useIps.gelocation.none'),
      },
      {
        id: 'City',
        value: 'City',
        name: t('trackerSettings.privacy.anonimization.useIps.gelocation.city'),
      },
      {
        id: 'Region',
        value: 'Region',
        name: t('trackerSettings.privacy.anonimization.useIps.gelocation.region'),
      },
      {
        id: 'Country',
        value: 'Country',
        name: t('trackerSettings.privacy.anonimization.useIps.gelocation.country'),
      },
    ];
  }

  async handleSubmitCheckbox(
    values,
    { setFieldValue, setSubmitting }: {
      setSubmitting: Function
      setFieldValue: Function
    },
  ) {
    const {
      trackerSettings,
      globalTrackerSettings,
      appId,
    } = this.props;
    const fieldName = Object.keys(values)[0].split('_global')[0];

    try {
      if (values[`${fieldName}_global`]) {
        await trackerSettings.deleteSettingsForApp(appId, fieldName);
      } else {
        const value = get(
          globalTrackerSettings.attributes,
          fieldName,
          DEFAULT_VALUES[fieldName],
        );
        await trackerSettings.updateSettingsForApp(appId, {
          [fieldName]: value,
        });
      }
    } catch (err) {
      setFieldValue(`${fieldName}_global`, !values[`${fieldName}_global`], false);
    }

    setSubmitting(false);
  }

  async handleUpdateSettings(values: any, {
    setErrors,
    setSubmitting,
    setFieldValue,
  }: any) {
    const {
      appTrackerSettings,
      trackerSettings,
      appId,
    } = this.props;

    try {
      if (values.anonymize_visitor_ip_level !== undefined) {
        let anonymizeIpLevel;

        switch (values.anonymize_visitor_ip_level) {
          case false:
            anonymizeIpLevel = anonymizeVisitorIpLevels.noAnonymize;
            break;
          case true:
            anonymizeIpLevel = anonymizeVisitorIpLevels.firstTwoBytes;
            break;
          default:
            anonymizeIpLevel = Number(values.anonymize_visitor_ip_level);
        }

        if (anonymizeIpLevel !== appTrackerSettings.attributes.anonymize_visitor_ip_level) {
          await trackerSettings.updateSettingsForApp(appId, {
            anonymize_visitor_ip_level: anonymizeIpLevel,
          });
        }
      } else {
        await trackerSettings.updateSettingsForApp(appId, values);
      }
    } catch (err: any) {
      setFieldValue(
        Object.keys(values)[0],
        (appTrackerSettings.attributes as any)[Object.keys(values)[0]],
        false,
      );
      setErrors(err.errors);
    }

    setSubmitting(false);
  }

  renderIpOptions() {
    const {
      appTrackerSettings,
      globalTrackerSettings,
      t,
    } = this.props;

    const anonymizeVisitorIpLevelValue = get(
      appTrackerSettings.attributes,
      'anonymize_visitor_ip_level',
      get(
        globalTrackerSettings.attributes,
        'anonymize_visitor_ip_level',
        anonymizeVisitorIpLevels.firstTwoBytes,
      ),
    );

    const anonymizeVisitorIpLevelGlobal = (
      appTrackerSettings.attributes?.anonymize_visitor_ip_level === undefined
    );

    const anonymizeVisitorIpLevelOptionsDisabled = (
      anonymizeVisitorIpLevelGlobal
      || anonymizeVisitorIpLevelValue === anonymizeVisitorIpLevels.noAnonymize
    );

    return (
      <Card>
        <Card.Section subdued>
          <Stack vertical>
            <Stack.Item>
              <Stack wrap verticalAlignment="center">
                <Stack.Item fill>
                  <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">
                    {t('trackerSettings.privacy.anonimization.anonimizeIdAdresses.title')}
                  </Text>
                  <Text as="p">
                    {t('trackerSettings.privacy.anonimization.anonimizeIdAdresses.description')}
                  </Text>
                </Stack.Item>
                <Stack.Item>
                  <Action.AutoSavedSwitcherControl
                    autoFocus
                    id="app-settings-tracker-settings-anonimize-ips-form"
                    initialValue={anonymizeVisitorIpLevelValue !== 0}
                    disabled={anonymizeVisitorIpLevelGlobal}
                    disabledActionTooltipText={t('app.details.settings.general.use-global-settings.block-reason')}
                    name="anonymize_visitor_ip_level"
                    onSubmit={this.handleUpdateSettings}
                  />
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <Stack spacing="narrow">
                <Stack.Item>
                  <Action.AutoSavedCheckboxControl
                    id="app-settings-tracker-settings-anonimize-ips-use-globals"
                    onSubmit={this.handleSubmitCheckbox}
                    initialValue={anonymizeVisitorIpLevelGlobal}
                    label={t('app.details.settings.general.use-global-settings.label')}
                    name="anonymize_visitor_ip_level_global"
                  />
                </Stack.Item>
                <Stack.Item>
                  <Tooltip content={t('app.details.settings.general.use-global-settings.tooltip') as any}>
                    <div>
                      <Icon name="info-circle" />
                    </div>
                  </Tooltip>
                </Stack.Item>
              </Stack>
            </Stack.Item>
          </Stack>
        </Card.Section>
        <Card.Section subdued>
          <Stack vertical spacing="narrow">
            <Stack.Item>
              <AutoSavedRadioButtonGroupControl
                name="anonymize_visitor_ip_level"
                initialValue={
                  anonymizeVisitorIpLevelValue || DEFAULT_VALUES.anonymize_visitor_ip_level
                }
                onSubmit={this.handleUpdateSettings}
                options={[
                  {
                    id: '1',
                    value: String(anonymizeVisitorIpLevels.firstByte),
                    label: t('trackerSettings.privacy.anonimization.firstOption'),
                    disabled: anonymizeVisitorIpLevelOptionsDisabled,
                  },
                  {
                    id: '2',
                    value: String(anonymizeVisitorIpLevels.firstTwoBytes),
                    label: t('trackerSettings.privacy.anonimization.secondOption'),
                    disabled: anonymizeVisitorIpLevelOptionsDisabled,
                  },
                  {
                    id: '3',
                    value: String(anonymizeVisitorIpLevels.firstThreeBytes),
                    label: t('trackerSettings.privacy.anonimization.thirdOption'),
                    disabled: anonymizeVisitorIpLevelOptionsDisabled,
                  },
                  {
                    id: '4',
                    value: String(anonymizeVisitorIpLevels.firstFourBytes),
                    label: t('trackerSettings.privacy.anonimization.fourthOption'),
                    disabled: anonymizeVisitorIpLevelOptionsDisabled,
                  },
                ]}
              />
            </Stack.Item>
            <Stack.Item>
              <Text as="p" size={12} color="InkLighter" dataId="Caption">
                {t('trackerSettings.privacy.anonimization.options.description')}
              </Text>
            </Stack.Item>
          </Stack>
        </Card.Section>
        <Card.Section subdued>
          <Stack vertical>
            <Stack.Item>
              <Stack wrap verticalAlignment="center">
                <Stack.Item fill>
                  <Text
                    as="h3"
                    size={16}
                    weight={700}
                    wrapType="noWrapAndCutText"
                    dataId="Header.small"
                    {...(!anonymizeVisitorIpLevelValue ? { color: 'InkLightest' } : {})}
                  >
                    {t('trackerSettings.privacy.anonimization.useIps.title')}
                  </Text>
                  <Text
                    as="p"
                    {...(anonymizeVisitorIpLevelValue ? {} : { color: 'InkLighter' })}
                  >
                    {t('trackerSettings.privacy.anonimization.useIps.description')}
                  </Text>
                </Stack.Item>
                <Stack.Item>
                  <div style={{ minWidth: '152px' }}>
                    <Action.AutoSavedSelectControl
                      id="app-settings-tracker-settings-use-anonimized-ips-form"
                      initialValue={
                        appTrackerSettings
                          .attributes.anonymize_visitor_geolocation_level !== undefined
                          ? appTrackerSettings.attributes.anonymize_visitor_geolocation_level
                          : globalTrackerSettings.attributes.anonymize_visitor_geolocation_level
                      }
                      name="anonymize_visitor_geolocation_level"
                      options={[{ id: 0, name: '', items: this.geolocationLevels }]}
                      disabled={
                        !anonymizeVisitorIpLevelValue
                        || appTrackerSettings.attributes.anonymize_visitor_geolocation_level
                        === undefined
                      }
                      disabledActionTooltipText={t(
                        'app.details.settings.general.use-global-settings.block-reason',
                      )}
                      onSubmit={this.handleUpdateSettings}
                    />
                  </div>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <Stack spacing="narrow">
                <Stack.Item>
                  <Action.AutoSavedCheckboxControl
                    id="app-settings-anonymize-visitor-geolocation-level-use-globals"
                    onSubmit={this.handleSubmitCheckbox}
                    initialValue={
                      appTrackerSettings
                        .attributes?.anonymize_visitor_geolocation_level === undefined
                    }
                    disabled={!anonymizeVisitorIpLevelValue}
                    label={t('app.details.settings.general.use-global-settings.label')}
                    name="anonymize_visitor_geolocation_level_global"
                  />
                </Stack.Item>
                <Stack.Item>
                  <Tooltip content={t('app.details.settings.general.use-global-settings.tooltip') as any}>
                    <div>
                      <Icon name="info-circle" />
                    </div>
                  </Tooltip>
                </Stack.Item>
              </Stack>
            </Stack.Item>
          </Stack>
        </Card.Section>
      </Card>
    );
  }

  render() {
    const {
      appTrackerSettings,
      globalTrackerSettings,
      t,
    } = this.props;

    const ipTrackingValue = get(
      appTrackerSettings.attributes,
      'set_ip_tracking',
      get(
        globalTrackerSettings.attributes,
        'set_ip_tracking',
        DEFAULT_VALUES.set_ip_tracking,
      ),
    );

    return (
      <Card>
        <Card.Section>
          <Stack vertical>
            <Stack.Item fill>
              <Stack wrap verticalAlignment="center">
                <Stack.Item fill>
                  <Stack vertical spacing="narrow">
                    <Stack.Item>
                      <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">
                        {t('apps.details.settings.ip-tracking.title')}
                      </Text>
                    </Stack.Item>
                    <Stack.Item>
                      <Text as="p">
                        {t('apps.details.settings.ip-tracking.description')}
                      </Text>
                    </Stack.Item>
                  </Stack>
                </Stack.Item>
                <Stack.Item>
                  <Action.AutoSavedSwitcherControl
                    id="app-settings-tracker-settings-ip-tracking"
                    initialValue={ipTrackingValue}
                    name="set_ip_tracking"
                    disabled={
                      appTrackerSettings.attributes.set_ip_tracking === undefined
                    }
                    disabledActionTooltipText={t('app.details.settings.general.use-global-settings.block-reason')}
                    onSubmit={this.handleUpdateSettings}
                  />
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item fill>
              <Stack spacing="narrow">
                <Stack.Item>
                  <Action.AutoSavedCheckboxControl
                    id="app-settings-tracker-settings-ip-tracking-use-globals"
                    onSubmit={this.handleSubmitCheckbox}
                    initialValue={
                      appTrackerSettings.attributes?.set_ip_tracking === undefined
                    }
                    label={t('app.details.settings.general.use-global-settings.label')}
                    name="set_ip_tracking_global"
                  />
                </Stack.Item>
                <Stack.Item>
                  <Tooltip content={t('app.details.settings.general.use-global-settings.tooltip') as any}>
                    <div>
                      <Icon name="info-circle" />
                    </div>
                  </Tooltip>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            {!ipTrackingValue || (
              <Stack.Item>{this.renderIpOptions()}</Stack.Item>
            )}
          </Stack>
        </Card.Section>
      </Card>
    );
  }
}

export default withInject({
  trackerSettings: 'TrackerSettingsCrate.tracker',
})(Privacy);
